import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { isxOperations } from "store";
import { default as _isEqual } from "lodash/isEqual";
import { default as _cloneDeep } from "lodash/cloneDeep";
import { default as _pick } from "lodash/pick";
import { default as _get } from "lodash/get";
import { default as _set } from "lodash/set";
import { default as _merge } from "lodash/merge";

const _pickDeep = (obj, template) => {
  const proxiedValues = {};
  const _recursePaths = (subTemplate, currentPath) => {
    Object.entries(subTemplate).forEach(([key, entry]) => {
      const newPath = [...currentPath, key];
      if (!entry) {
        const val = _get(obj, newPath);
        _set(proxiedValues, newPath, val);
      } else {
        _recursePaths(entry, newPath);
      }
    });
  };
  _recursePaths(template, []);
  return proxiedValues;
};

const useWidgetProxy = (widget, optionsTemplate = null) => {
  const [proxy, setProxy] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const dirtyAttrs = useRef(new Set());

  const dispatch = useDispatch();

  const updateWidget = (wprops) =>
    dispatch(isxOperations.updateWidget(widget.guuid, wprops));

  const updateProxy = (wprops) => {
    setProxy((proxy) => ({ ...proxy, ...wprops }));
    Object.keys(wprops).forEach((attr) => {
      dirtyAttrs.current.add(attr);
    });
    setIsDirty(true);
  };

  const commit = () => {
    if (dirtyAttrs.current.size > 0) {
      if (optionsTemplate) {
        // need to merge with widget since otherwise write to db will be lossy
        const payload = _cloneDeep(_pick(widget, Object.keys(optionsTemplate)));
        // only want those properties explicitly specified
        _merge(payload, _pickDeep(proxy, optionsTemplate));
        updateWidget(payload);
      } else {
        updateWidget(_pick(proxy, [...dirtyAttrs.current]));
      }
      dirtyAttrs.current.clear();
      setIsDirty(false);
    }
  };

  const rollback = () => {
    if (dirtyAttrs.current.size > 0) {
      setProxy(_cloneDeep(widget));
      dirtyAttrs.current.clear();
      setIsDirty(false);
    }
  };

  useEffect(() => {
    if (dirtyAttrs.current.size === 0) {
      setProxy((proxy) =>
        !_isEqual(widget, proxy) ? _cloneDeep(widget) : proxy
      );
    }
  }, [widget]);

  return { proxy, updateProxy, commit, rollback, isDirty };
};

export default useWidgetProxy;
